import React, { useCallback, useEffect, useState } from 'react';
import Facet from './Facet';
import Result from './Result';
import Selectro from 'components/common/Selectro';
import Immutable from 'immutable';
import classnames from 'classnames';
import useScrollPositioning from './useScrollPositioning';
import useSearch from './useSearch';
import renderIf from 'render-if';
import Spinner from '../common/Spinner';
import { useEngagementSession } from './useEngagementSession';
import AccountSearchAiChatCta from './AccountSearchAiChat/AccountSearchAiChatCta';

// Global cache because we only expect to have one of these on a page at any given time.
// This helps us mount & unmount the component with the same state.
const newCache = () => {
  return {
    results: [],
    enhancedResults: [],
    customProjectFacet: () => {
      return null;
    },
    customSearchableTypeFacet: () => {
      return null;
    },
    customWorkflowStatusFacet: () => {
      return null;
    },
    scroll: {
      top: 0,
      query: null,
    },
    sortBy: 'recent',
    searchableTypeFacets: [],
    projectFacets: [],
    workflowStatusFacets: [],
    assignedToUserId: window?.currentUser?.id,
    currentPage: 1,
    name: 'My Work',
  };
};

let cache = newCache();
document.addEventListener('aha.fred.error', () => {
  cache = newCache();
});

const MyWorkSearch = ({ query }) => {
  const engagementEvent = useEngagementSession('Feature-7412685681697076782');

  useEffect(() => {
    if (cache.query !== query) {
      engagementEvent('QUERY_CHANGED')(query.length);
    }
  }, [engagementEvent, query]);

  const {
    results,
    enhancedResults,
    projectFacets,
    searchableTypeFacets,
    customProjectFacet,
    workflowStatusFacets,
    setCustomProjectFacet,
    customSearchableTypeFacet,
    setCustomSearchableTypeFacet,
    customWorkflowStatusFacet,
    setCustomWorkflowStatusFacet,
    loadingResults,
    fetchMore,
    fetchingMore,
  } = useSearch({
    query,
    resultsQuery: useCallback(
      ({
        customSearchableTypeFacet,
        customProjectFacet,
        customWorkflowStatusFacet,
      }) => {
        const data = {
          q: `${query}*`,
          searchable_type: customSearchableTypeFacet,
          assigned_to_user_id: window.currentUser.id,
        };

        if (customProjectFacet || customProjectFacet === '') {
          data.project_id = customProjectFacet;
        }

        if (customWorkflowStatusFacet) {
          data.workflow_status_ids = customWorkflowStatusFacet;
        }

        data.include_facets = true;
        return data;
      },
      [query]
    ),
    facetsQuery: useCallback(
      ({
        customSearchableTypeFacet,
        customProjectFacet,
        customWorkflowStatusFacet,
      }) => {
        const data = {
          q: `${query}*`,
          searchable_type: customSearchableTypeFacet,
          assigned_to_user_id: window.currentUser.id,
        };

        if (customProjectFacet || customProjectFacet === '') {
          data.project_id = customProjectFacet;
        }

        if (customWorkflowStatusFacet) {
          data.workflow_status_ids = customWorkflowStatusFacet;
        }
        return data;
      },
      [query]
    ),
    cache,
  });

  const [currentSearchableType, setCurrentSearchableType] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);

  const realSetCustomSearchableTypeFacet = engagementEvent(
    'SET_CUSTOM_SEARCHABLE_TYPE',
    setCustomSearchableTypeFacet
  );
  const realSetCustomProjectFacet = engagementEvent(
    'SET_CUSTOM_PROJECT_FACET',
    setCustomProjectFacet
  );

  useEffect(() => {
    if (searchableTypeFacets.length === 0 && customSearchableTypeFacet) return;

    setCurrentSearchableType(
      searchableTypeFacets.find(
        searchable_type => searchable_type[0].key === customSearchableTypeFacet
      )
    );
  }, [customSearchableTypeFacet, searchableTypeFacets]);

  const knnOnClick = useCallback(
    ({ index }) => {
      engagementEvent('CLICK_KNN')(index);
    },
    [engagementEvent]
  );

  const resultOnClick = useCallback(
    ({ index }) => {
      engagementEvent('CLICK_RESULT')(index);
    },
    [engagementEvent]
  );

  useEffect(() => {
    if (projectFacets.length === 0 && customProjectFacet) return;

    setCurrentProject(
      projectFacets.find(
        project => project[0] && project[0].id === customProjectFacet
      )
    );
  }, [customProjectFacet, projectFacets]);

  const { setScroll, resultsRef } = useScrollPositioning({
    cache,
    query,
    fetchMore,
  });

  const showResults = ({ results, enhancedResults }) => {
    if (!results || results.length === 0) return [];

    const displayedResults = results.map((result, index) => {
      return (
        <Result
          key={result.url}
          // @ts-ignore
          result={result}
          additionalOnclick={() => {
            if (
              result.matched_queries &&
              result.matched_queries.length === 1 &&
              result.matched_queries[0] === 'knn'
            ) {
              knnOnClick({ result, index });
            } else {
              resultOnClick({ result, index });
            }
          }}
          showProject={!customProjectFacet}
          timestampCopy={'Last updated:'}
        />
      );
    });

    const enhancedResultsIndexes = [2, 5, 8];
    for (const [index, result] of enhancedResults.entries()) {
      if (!results.find(({ url }) => url === result.url)) {
        displayedResults.splice(
          enhancedResultsIndexes.shift(),
          0,
          <Result
            key={result.url}
            // @ts-ignore
            result={result}
            additionalOnclick={() => {
              knnOnClick({ result, index });
            }}
            showProject={!customProjectFacet}
            timestampCopy={'Last updated:'}
          />
        );
      }
      if (enhancedResultsIndexes.length === 0) {
        break;
      }
    }

    return displayedResults;
  };

  const filterAssistantEnabled =
    window.featureFlags?.search_filters_ai_assistant &&
    window.currentAccount?.aiAnalysisOfSearchResultsEnabled &&
    !window.currentAccount?.onlyDiscoveryFlavor;

  const hasAnyFacets = Boolean(
    customSearchableTypeFacet ||
      searchableTypeFacets.length !== 0 ||
      customProjectFacet ||
      projectFacets.length !== 0 ||
      customWorkflowStatusFacet ||
      workflowStatusFacets.length !== 0
  );

  return renderIf(window.currentUser)(
    <>
      <div className='search'>
        <div
          className={classnames({
            results: true,
            'results--loading': loadingResults,
            'results--fetching-more': fetchingMore,
            'tw-basis-[70%]': true,
            'tw-flex-grow': true,
          })}
          ref={resultsRef}
          onScroll={e => {
            // @ts-ignore
            setScroll(e.target.scrollTop);
          }}
        >
          {renderIf(loadingResults)(<Spinner />)}

          {renderIf(!loadingResults)(
            <>
              {renderIf(!query?.length && filterAssistantEnabled)(
                <AccountSearchAiChatCta />
              )}
              {showResults({ results, enhancedResults }).map(c => c)}

              {renderIf(results.length === 0 && query.length !== 0)(
                <div className='empty' key='empty'>
                  <h3>No results</h3>
                </div>
              )}

              {renderIf(results.length === 0 && query.length === 0)(
                <div className='empty' key='empty'>
                  <h3>Start typing to search records</h3>
                </div>
              )}

              {renderIf(fetchingMore)(<Spinner small />)}
            </>
          )}
        </div>
        {hasAnyFacets && (
          <div className='facets tw-basis-[30%]'>
            <h5>Filter by</h5>
            {renderIf(searchableTypeFacets.length !== 0)(<h6>Record type</h6>)}

            <ul>
              {renderIf(customSearchableTypeFacet && currentSearchableType)(
                () => {
                  return (
                    <Facet
                      onClick={() => realSetCustomSearchableTypeFacet(null)}
                      name={
                        currentSearchableType[0] &&
                        currentSearchableType[0].name
                      }
                      count={currentSearchableType && currentSearchableType[1]}
                      key={customSearchableTypeFacet}
                      active
                      showCount={results.length !== 0}
                    />
                  );
                }
              )}
              {renderIf(!customSearchableTypeFacet)(
                searchableTypeFacets
                  .slice(0, 9)
                  .map(([{ key, name }, count]) => {
                    const facetActive = customSearchableTypeFacet === key;

                    return (
                      <Facet
                        onClick={() =>
                          realSetCustomSearchableTypeFacet(
                            facetActive ? null : key
                          )
                        }
                        name={name}
                        count={count}
                        key={key}
                        active={facetActive}
                        showCount
                      />
                    );
                  })
              )}

              {renderIf(
                !customSearchableTypeFacet && searchableTypeFacets.length > 9
              )(
                <li className='facet'>
                  <Selectro
                    fetchOptions={Immutable.List(
                      searchableTypeFacets.map(([{ key, name }, count]) => [
                        `${name} (${count})`,
                        key,
                      ])
                    )}
                    multiple={false}
                    headersClickable={false}
                    selectedElements={
                      customSearchableTypeFacet
                        ? Immutable.List([
                            customSearchableTypeFacet,
                            customSearchableTypeFacet,
                          ])
                        : Immutable.List([])
                    }
                    select={item =>
                      realSetCustomSearchableTypeFacet(item.value)
                    }
                    unselect={() => realSetCustomSearchableTypeFacet(null)}
                    clear={() => realSetCustomSearchableTypeFacet(null)}
                    defaultLabel='Choose record type'
                    placeholder='Select record type'
                    classes={['input-xlarge facet__picker']}
                    showSearch
                  />
                </li>
              )}
            </ul>

            {renderIf(projectFacets.length !== 0)(<h6>Workspace</h6>)}
            <ul>
              {renderIf(customProjectFacet && currentProject)(() => {
                return (
                  <Facet
                    onClick={() => realSetCustomProjectFacet(null)}
                    name={currentProject[0] && currentProject[0].name}
                    count={currentProject[1]}
                    key={customProjectFacet}
                    active
                    showCount={results.length !== 0}
                  />
                );
              })}
              {renderIf(!customProjectFacet)(
                projectFacets.slice(0, 3).map(([project, count]) => {
                  const facetActive = customProjectFacet === project.id;

                  return (
                    <Facet
                      onClick={() =>
                        realSetCustomProjectFacet(
                          facetActive ? null : project.id
                        )
                      }
                      name={project.name}
                      count={count}
                      key={project.id}
                      active={facetActive}
                      showCount
                    />
                  );
                })
              )}

              {renderIf(!customProjectFacet && projectFacets.length > 3)(
                <li className='facet'>
                  <Selectro
                    fetchOptions={Immutable.List(
                      projectFacets.map(([project, count]) => [
                        `${project.name} (${count})`,
                        project.id,
                      ])
                    )}
                    multiple={false}
                    headersClickable={false}
                    selectedElements={
                      currentProject
                        ? Immutable.List([
                            currentProject.name,
                            customProjectFacet,
                          ])
                        : Immutable.List([])
                    }
                    select={item => realSetCustomProjectFacet(item.value)}
                    unselect={() => realSetCustomProjectFacet(null)}
                    clear={() => realSetCustomProjectFacet(null)}
                    defaultLabel='Choose workspace'
                    placeholder='Select workspace'
                    classes={['input-xlarge facet__picker']}
                    showSearch
                  />
                </li>
              )}
            </ul>

            {renderIf(workflowStatusFacets.length !== 0)(<h6>Status</h6>)}
            <ul>
              {renderIf(
                workflowStatusFacets && workflowStatusFacets.length > 0
              )(
                (workflowStatusFacets || []).map(([status, count]) => {
                  const facetActive =
                    customWorkflowStatusFacet === status.id ||
                    // ts didn't think Set has an intersection method, and who am I to argue?
                    (customWorkflowStatusFacet?.split(',') || []).filter(
                      value => status.id.split(',').includes(value)
                    ).length > 0;
                  // Handle cases where we still have a compound facet selected when we select a project which reduces the available facets, like if you selected a status filter first and then selected a type filter.

                  return (
                    <Facet
                      onClick={() =>
                        setCustomWorkflowStatusFacet(
                          facetActive ? null : status.id
                        )
                      }
                      name={status.name}
                      count={count}
                      key={status.id}
                      active={facetActive}
                      showCount
                    />
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default MyWorkSearch;
